import React, { useCallback, useMemo } from 'react';
import { ReactFlow, ReactFlowProvider, Background, Controls, type Connection, type Edge, type Node } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { Clock, UserPlus, Users, Bell, GitBranch, MessageSquare, Phone, IdCard } from 'lucide-react';
import { buildRuleFlow } from './automationFlowLayout';
import {
  automationFlowNodeTypes,
  type FlowSharedActions,
  type FlowConditionActions,
  type FlowSettingsActions,
} from './AutomationFlowNodes';
import type { CategoryOption } from './CategoryAutocompleteInput';
import {
  updateNode,
  removeNode,
  insertAfter,
  convertToBranch,
  appendToCase,
  addCase,
  removeCase,
  updateCase,
  updateBranchField,
  makeActionNode,
  type ActionNode,
} from './automationActionTree';

// Component palette (2026-08-26) — "right side panel, components you can
// add to the chain" per stakeholder feedback, replacing the old flow's only
// way to add a step (a generic "+ Action" ghost node defaulting to Assign to
// Agent, then hand-picking a type from its dropdown). Clicking a palette
// item appends that specific action type to the END of the top-level chain
// directly — same underlying `onChange([...actions, node])` the ghost node
// already used, just parameterized by type instead of always defaulting to
// assign_agent. Deliberately a fixed palette of the most-used types, not
// every ACTION_TYPES entry — the type dropdown on any resulting node still
// covers the full list for less-common ones.
const PALETTE_ITEMS: Array<{ type: string; label: string; icon: React.ComponentType<{ className?: string }> }> = [
  { type: 'wait', label: 'Wait', icon: Clock },
  { type: 'notify_agent', label: 'Notify Agent', icon: Bell },
  { type: 'notify_manager', label: 'Notify Manager', icon: Bell },
  { type: 'assign_agent', label: 'Assign Agent', icon: UserPlus },
  { type: 'assign_team', label: 'Assign Team', icon: Users },
  { type: 'send_message', label: 'Send Message', icon: MessageSquare },
  { type: 'call_agent', label: 'Call Agent', icon: Phone },
  // 2026-09-04 (Docs/RELEASE_NOTES_2026-09-04_PROFILE_NUDGE.md): the
  // deterministic name/age/ID-card-photo nudge -- automation_actions.py's
  // request_profile_field handler.
  { type: 'request_profile_field', label: 'Request Profile Field', icon: IdCard },
  { type: 'branch', label: 'Branch', icon: GitBranch },
];

interface Props {
  triggerEvent: string;
  // 2026-09-01 (live feedback — "everything should be in canvas"): the
  // rule-level settings, now edited in place inside the Trigger node
  // (TriggerFlowNode) instead of a separate form above the canvas. Owned
  // by AutomationRuleEditor.tsx exactly like `actions`/`conditions` are.
  formData: FlowSettingsActions['formData'];
  onFormDataChange: (next: FlowSettingsActions['formData']) => void;
  conditions: any[];
  onConditionsChange: (next: any[]) => void;
  actions: ActionNode[];
  onChange: (next: ActionNode[]) => void;
  // 2026-09-01 (Phase 2, freeform drag): {node_id: {x, y}} for nodes the
  // admin has manually repositioned — a node absent here still gets the
  // layout engine's own auto-computed position (see the nodes useMemo
  // below). Persisted on the rule itself (AutomationRuleEditor.tsx),
  // purely for display — never read by execution.
  layout: Record<string, { x: number; y: number }>;
  onLayoutChange: (next: Record<string, { x: number; y: number }>) => void;
  isTicketEvent: boolean;
  agents: any[];
  teams: any[];
  labels: any[];
  categories: CategoryOption[];
  templates: Array<{ id: number; name: string; language?: string }>;
  channels: Array<{ id: number; name: string }>;
}

const DEFAULT_CONDITION = { field_name: 'status', operator: 'equal_to', expected_value: '', group_operator: 'AND' };

/** Recursively counts real (non-empty) actions under a subtree — used only
 * to decide whether deleting a branch needs a confirm prompt. */
function countDescendantActions(node: ActionNode): number {
  if (node.action_type !== 'branch' || !node.action_parameters) return 0;
  const params = node.action_parameters;
  let count = 0;
  for (const c of params.cases || []) count += (c.actions || []).length;
  count += (params.default_actions || []).length;
  return count;
}

function findNode(list: ActionNode[], id: string): ActionNode | null {
  for (const n of list) {
    if (n._id === id) return n;
    if (n.action_type === 'branch' && n.action_parameters) {
      for (const c of n.action_parameters.cases || []) {
        const found = findNode(c.actions || [], id);
        if (found) return found;
      }
      const found = findNode(n.action_parameters.default_actions || [], id);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Node-graph view of a rule's action tree ("join the nodes" — 2026-08-08).
 * Renders the same `actions` array the flat form edits, as a real diamond-
 * shaped flow (branches fan out per case and fan back in on whatever
 * follows, matching automation_engine.execute_action_list's actual runtime
 * order).
 *
 * 2026-09-01: connecting/disconnecting is real — drag a new connection
 * from any node onto another to move it there (onConnect), or select a
 * connection and delete it to remove the step it points at
 * (onEdgesDelete). Both reuse the exact tree operations (removeNode/
 * insertAfter/appendToCase/removeCase) the click-driven controls inside
 * each node already call.
 *
 * Positioning is now freeform too (live feedback: "whole page canvas...
 * with draggable"), layered ON TOP of the same auto-layout engine rather
 * than replacing it: a node the admin has manually dragged keeps that
 * exact position (persisted in `layout`, see AutomationRuleEditor.tsx's
 * canvas_layout), while any node with no stored position — every new node,
 * and every node for a rule that's never been dragged at all — still gets
 * the layout engine's own computed position, same as before this existed.
 * This is why buildRuleFlow/layoutActionList still run on every render
 * even though dragging is on: they're the FALLBACK position source, not a
 * competing one — mutating a stored position is a plain merge into
 * `layout`, never a replacement for the computed layout.
 */
const AutomationFlowEditor: React.FC<Props> = ({
  triggerEvent, formData, onFormDataChange, conditions, onConditionsChange, actions, onChange, layout,
  onLayoutChange, isTicketEvent, agents, teams, labels, categories, templates, channels,
}) => {
  const settingsActions: FlowSettingsActions = useMemo(() => ({
    formData,
    onChangeField: (key, value) => onFormDataChange({ ...formData, [key]: value }),
  }), [formData, onFormDataChange]);

  const conditionActions: FlowConditionActions = useMemo(() => ({
    isTicketEvent, categories,
    onAddCondition: () => onConditionsChange([...conditions, { ...DEFAULT_CONDITION }]),
    onRemoveCondition: (index) => onConditionsChange(conditions.filter((_, i) => i !== index)),
    onChangeCondition: (index, field, value) => {
      const next = [...conditions];
      next[index] = { ...next[index], [field]: value };
      onConditionsChange(next);
    },
  }), [conditions, onConditionsChange, isTicketEvent, categories]);

  const shared: FlowSharedActions = useMemo(() => ({
    isTicketEvent, agents, teams, labels, categories, templates, channels,
    onChangeActionType: (id, actionType) => onChange(updateNode(actions, id, { action_type: actionType })),
    onChangeActionParam: (id, key, value) =>
      onChange(updateNode(actions, id, (n) => ({ action_parameters: { ...(n.action_parameters || {}), [key]: value } }))),
    onDeleteAction: (id, isBranch) => {
      if (isBranch) {
        const node = findNode(actions, id);
        const count = node ? countDescendantActions(node) : 0;
        if (count > 0 && !window.confirm(`Delete this branch and its ${count} nested action(s)?`)) return;
      }
      onChange(removeNode(actions, id));
    },
    onConvertToBranch: (id) => onChange(convertToBranch(actions, id)),
    onInsertActionAfter: (id) => onChange(insertAfter(actions, id, makeActionNode())),
    onChangeBranchField: (branchId, field) => onChange(updateBranchField(actions, branchId, { field })),
    onAddCase: (branchId) => onChange(addCase(actions, branchId)),
    onRemoveCase: (branchId, caseId) => onChange(removeCase(actions, branchId, caseId)),
    onUpdateCase: (branchId, caseId, patch) => onChange(updateCase(actions, branchId, caseId, patch)),
    onAddActionTo: (branchId, caseId) => {
      const node = makeActionNode();
      if (branchId === null) onChange([...actions, node]);
      else onChange(appendToCase(actions, branchId, caseId as string, node));
    },
  }), [actions, onChange, isTicketEvent, agents, teams, labels, categories, templates, channels]);

  // 2026-09-01 (live feedback: "especially the default ones they cannot be
  // separated"): trigger/condition used to be excluded here as "fixed
  // structural anchors" -- but the auto-computed layout (SETTINGS_ROW_H in
  // automationFlowLayout.ts) is a rough estimate of the Trigger node's real
  // rendered height, and when it undershoots, the Trigger and Conditions
  // nodes visually overlap with no way to fix it, since neither could be
  // dragged apart. Made both draggable too (with their own DragHandle, like
  // action/branch/case already have) as a manual escape hatch -- ghost "add
  // action" placeholders are still excluded, since they disappear the
  // moment they're used and repositioning one has no lasting meaning.
  const DRAGGABLE_NODE_TYPES = useMemo(() => new Set(['trigger', 'condition', 'action', 'branch', 'case']), []);

  const { nodes, edges } = useMemo(() => {
    const built = buildRuleFlow(triggerEvent, conditions, actions);
    const withData: Node[] = built.nodes.map((n) => {
      const stored = layout[n.id];
      const isDraggable = DRAGGABLE_NODE_TYPES.has(n.type || '');
      return {
        ...n,
        // Stored position wins when this node has been manually dragged;
        // otherwise fall back to the layout engine's own computed spot —
        // see this component's own docstring for why both coexist.
        position: stored || n.position,
        draggable: isDraggable,
        // 2026-09-01: scopes the drag gesture to each node's small grip
        // strip (AutomationFlowNodes.tsx's DragHandle) instead of the whole
        // card — see cardBase's note there for why a blanket `nodrag` on
        // the card previously made this dead. Only set for the types that
        // actually render that strip; omitted (not just false) for
        // non-draggable types so there's no dangling selector.
        ...(isDraggable ? { dragHandle: '.automation-drag-handle' } : {}),
        data: { ...n.data, actions: shared, conditionsActions: conditionActions, settingsActions },
      };
    });
    return { nodes: withData, edges: built.edges };
  }, [triggerEvent, conditions, actions, shared, conditionActions, settingsActions, layout, DRAGGABLE_NODE_TYPES]);

  // Persists exactly the node that was dragged — a plain merge into
  // `layout`, never wholesale replaced, so every other node's stored (or
  // not-yet-stored, still-auto-computed) position is untouched.
  const onNodeDragStop = useCallback((_event: unknown, node: Node) => {
    onLayoutChange({ ...layout, [node.id]: { x: node.position.x, y: node.position.y } });
  }, [layout, onLayoutChange]);

  // 2026-09-01 (Docs/AUTOMATION_VISUAL_WORKFLOW_BUILDER_PLAN.md Phase 2b,
  // "freely connect/disconnect"): dragging a wire from one node's source
  // handle to another node's target handle re-parents the TARGET to come
  // right after the SOURCE, wherever the source is in the tree (a real
  // action/branch node, a branch's case/Else header, or Conditions itself
  // for "make this the first top-level action"). Reuses the exact same
  // tree operations the click-driven UI already calls (removeNode/
  // insertAfter/appendToCase from automationActionTree.ts) — this is a new
  // INPUT gesture for existing, already-exercised mutations, not new tree
  // logic. `nodes` (not just `actions`) is a dependency because a case/
  // Else header's branchId/caseId live in that flow node's own `.data`,
  // not recoverable from its id string alone.
  const onConnect = useCallback((connection: Connection) => {
    const { source, target } = connection;
    if (!source || !target) return;

    const targetNode = findNode(actions, target);
    if (!targetNode) {
      // Can only re-attach a real action/branch node -- dragging a wire
      // onto a ghost "add action" placeholder or a Conditions/Trigger
      // node as the TARGET isn't a meaningful move (those aren't things
      // that live in the action tree to begin with).
      return;
    }
    const sourceFlowNode = nodes.find((n) => n.id === source);
    if (!sourceFlowNode || sourceFlowNode.id === target) return;

    let next = removeNode(actions, target);

    if (sourceFlowNode.type === 'trigger' || sourceFlowNode.type === 'condition') {
      next = [targetNode, ...next];
    } else if (sourceFlowNode.type === 'case' || sourceFlowNode.type === 'addAction') {
      const { branchId, caseId, isDefault } = sourceFlowNode.data as any;
      if (!branchId) return;
      next = appendToCase(next, branchId, isDefault ? 'default' : caseId, targetNode);
    } else if (findNode(next, source)) {
      // Source is a real action/branch node still present after removing
      // target -- the common case. (If source turns out to have been
      // INSIDE target's own subtree, it's gone too at this point, and this
      // check correctly no-ops the connection below rather than attaching
      // to a node that no longer exists.)
      next = insertAfter(next, source, targetNode);
    } else {
      return;
    }
    onChange(next);
  }, [actions, nodes, onChange]);

  // Selecting an edge and deleting it (Backspace/Delete, or a future edge
  // toolbar) removes whatever it points AT -- there's no valid "keep both
  // ends but sever the wire" state in a strictly ordered/branching rule
  // (an orphaned floating node can never execute), so "disconnect" and
  // "delete the downstream step" are the same operation here. A case/Else
  // header's own incoming edge removes that whole case (with the same
  // nested-action confirm as the existing per-node delete button);
  // anything else removes the single node.
  const onEdgesDelete = useCallback((deleted: Edge[]) => {
    let next = actions;
    for (const e of deleted) {
      const targetFlowNode = nodes.find((n) => n.id === e.target);
      if (!targetFlowNode) continue;
      if (targetFlowNode.type === 'case') {
        const { branchId, caseId, isDefault } = targetFlowNode.data as any;
        if (isDefault || !branchId || !caseId) continue; // "Else" always exists, not removable
        next = removeCase(next, branchId, caseId);
        continue;
      }
      if (targetFlowNode.type !== 'action' && targetFlowNode.type !== 'branch') continue;
      const node = findNode(next, e.target);
      if (!node) continue;
      if (node.action_type === 'branch') {
        const count = countDescendantActions(node);
        if (count > 0 && !window.confirm(`Delete this branch and its ${count} nested action(s)?`)) continue;
      }
      next = removeNode(next, e.target);
    }
    if (next !== actions) onChange(next);
  }, [actions, nodes, onChange]);

  // Appends a palette item to the end of the top-level chain. 'branch' needs
  // its required shape (field + at least one case) seeded — convertToBranch
  // already knows how to do that for an existing node, so a plain node is
  // appended first and immediately converted by its own fresh _id, rather
  // than duplicating that shape here.
  const handlePaletteAdd = (actionType: string) => {
    if (actionType === 'branch') {
      const node = makeActionNode();
      onChange(convertToBranch([...actions, node], node._id));
      return;
    }
    onChange([...actions, makeActionNode(actionType)]);
  };

  return (
    // 2026-09-01: was a fixed h-[560px] -- read as a small embedded box even
    // after the editor itself moved to a dedicated full-page route
    // (AutomationRuleEditorPage). AutomationRuleEditor's own outer wrapper
    // is plain block flow (space-y-6), not height-constrained, so this
    // can't just be h-full (that only resolves against a sized flex/grid
    // parent) -- a generous viewport-relative height is the honest fix
    // without restructuring that whole component's layout blind.
    <div className="flex h-[75vh] min-h-[560px] w-full gap-3">
      <div className="min-w-0 flex-1 overflow-hidden rounded-lg border border-border bg-muted/10">
        <ReactFlowProvider>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            nodeTypes={automationFlowNodeTypes}
            onConnect={onConnect}
            onEdgesDelete={onEdgesDelete}
            onNodeDragStop={onNodeDragStop}
            // Per-node `draggable` (set in the nodes useMemo above) is what
            // actually decides which node types can move -- this canvas-
            // level default only needs to not be false, since every node
            // here already carries its own explicit true/false.
            nodesDraggable
            nodesConnectable
            elementsSelectable
            panOnScroll
            zoomOnScroll={false}
            zoomOnPinch
            fitView
            fitViewOptions={{ padding: 0.2, maxZoom: 1 }}
            proOptions={{ hideAttribution: true }}
          >
            <Background gap={20} size={1} />
            <Controls showInteractive={false} />
          </ReactFlow>
        </ReactFlowProvider>
      </div>

      {/* Component palette — see PALETTE_ITEMS above. */}
      <div className="w-44 shrink-0 overflow-y-auto rounded-lg border border-border bg-card p-2.5">
        <p className="mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Add to chain
        </p>
        <div className="space-y-1.5">
          {PALETTE_ITEMS.map(({ type, label, icon: Icon }) => (
            <button
              key={type}
              type="button"
              onClick={() => handlePaletteAdd(type)}
              className="flex w-full items-center gap-2 rounded-md border border-border/60 bg-background px-2.5 py-2 text-left text-xs font-medium transition-colors hover:border-primary hover:bg-primary/5 hover:text-primary"
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              {label}
            </button>
          ))}
        </div>
        <p className="mt-3 px-1 text-[11px] leading-relaxed text-muted-foreground">
          Adds to the end of the chain below. Drag a step to reposition it, or drag a connection from one step onto
          another to reorder the chain — select a connection and press Delete to remove that step.
        </p>
      </div>
    </div>
  );
};

export default AutomationFlowEditor;
